// src/pages/VerifyEmailPage/VerifyEmailPage.tsx

import React, { useEffect, useState, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { API_BASE_URL } from '../../config';

const VerifyEmailPage: React.FC = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { login } = useAuth();
    const token = searchParams.get('token');

    const hasFetched = useRef(false);
    const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
    const [message, setMessage] = useState('Validando sua conta...');

    useEffect(() => {
        // SEGURANÇA: removido bypass ?token=demo que permitia ativar
        // conta sem validação do backend. Toda verificação agora passa pela
        // API.
        if (!token) {
            setStatus('error');
            setMessage('Link inválido ou incompleto.');
            return;
        }

        // --- REMOVIDO O BLOCO QUE CAUSAVA O FALSO POSITIVO ---
        // O código antigo verificava o localStorage aqui e retornava sucesso direto.
        // Agora vamos forçar a verificação com o backend sempre.

        if (hasFetched.current) return;
        hasFetched.current = true;

        const verifyAccount = async () => {
            try {
                const res = await fetch(`${API_BASE_URL}/api/auth/verify-email`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ token })
                });
                
                const data = await res.json();

                if (res.ok && data.token) {
                    // SUCESSO REAL: O Backend confirmou e mandou um token novo
                    login(data.token); 
                    setStatus('success');
                    setMessage('E-mail confirmado com sucesso! Entrando...');
                    setTimeout(() => navigate('/'), 2500);
                } else {
                    // ERRO REAL: Backend disse que o token não existe (já usado ou expirado)
                    setStatus('error');
                    // Usa a mensagem do backend ou fallback
                    setMessage(data.error || 'Este link já foi utilizado ou expirou.');
                }
            } catch (error) {
                setStatus('error');
                setMessage('Erro de conexão com o servidor.');
            }
        };

        verifyAccount();
    }, [token, login, navigate]);

    // ... (MANTENHA O RESTO DO RENDER E CSS IGUAL) ...
    // --- RENDERIZAÇÃO DOS ÍCONES ---
    const renderIcon = () => {
        if (status === 'loading') return <div className="spinner-container"><div className="spinner"></div></div>;
        if (status === 'success') return <div className="icon-container success"><svg viewBox="0 0 52 52" className="checkmark"><circle className="checkmark-circle" cx="26" cy="26" r="25" fill="none" /><path className="checkmark-check" fill="none" d="M14.1 27.2l7.1 7.2 16.7-16.8" /></svg></div>;
        if (status === 'error') return <div className="icon-container error"><svg viewBox="0 0 52 52" className="cross"><circle className="cross-circle" cx="26" cy="26" r="25" fill="none" /><path className="cross-line" fill="none" d="M16 16 36 36" /><path className="cross-line" fill="none" d="M36 16 16 36" /></svg></div>;
    };

    return (
        <div style={{
            height: '100vh', 
            width: '100vw', 
            background: '#131313 url(https://i.postimg.cc/j5mc2DgB/64dbfb2969016e7fb773950669225700.gif.png)',
            backgroundBlendMode: 'hard-light',
            backgroundPosition: '0',
            backgroundRepeat: 'no-repeat',
            backgroundSize: 'cover',
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            color: '#fff',
            fontFamily: "'Inter', sans-serif"
        }}>
            {/* ... (MANTENHA O STYLE TAG IGUAL) ... */}
            <style>{`
                @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
                .spinner-container { width: 80px; height: 80px; margin: 0 auto 25px auto; }
                .spinner { width: 100%; height: 100%; border: 4px solid rgba(255, 255, 255, 0.1); border-left-color: #635bff; border-radius: 50%; animation: spin 1s linear infinite; }
                @keyframes spin { 100% { transform: rotate(360deg); } }
                .icon-container { width: 80px; height: 80px; margin: 0 auto 25px auto; }
                .checkmark { width: 80px; height: 80px; border-radius: 50%; display: block; stroke-width: 2.5; stroke: #fff; stroke-miterlimit: 10; box-shadow: inset 0px 0px 0px #4cd137; animation: fillSuccess .4s ease-in-out .4s forwards, scale .3s ease-in-out .9s both; }
                .checkmark-circle { stroke-dasharray: 166; stroke-dashoffset: 166; stroke-width: 2.5; stroke-miterlimit: 10; stroke: #4cd137; fill: none; animation: stroke .6s cubic-bezier(0.65, 0, 0.45, 1) forwards; }
                .checkmark-check { transform-origin: 50% 50%; stroke-dasharray: 48; stroke-dashoffset: 48; animation: stroke .3s cubic-bezier(0.65, 0, 0.45, 1) .8s forwards; }
                .cross { width: 80px; height: 80px; border-radius: 50%; display: block; stroke-width: 2.5; stroke: #fff; stroke-miterlimit: 10; box-shadow: inset 0px 0px 0px #ff5252; animation: fillError .4s ease-in-out .4s forwards, shake .3s ease-in-out .9s both; }
                .cross-circle { stroke-dasharray: 166; stroke-dashoffset: 166; stroke-width: 2.5; stroke-miterlimit: 10; stroke: #ff5252; fill: none; animation: stroke .6s cubic-bezier(0.65, 0, 0.45, 1) forwards; }
                .cross-line { transform-origin: 50% 50%; stroke-dasharray: 48; stroke-dashoffset: 48; animation: stroke .3s cubic-bezier(0.65, 0, 0.45, 1) .8s forwards; }
                @keyframes stroke { 100% { stroke-dashoffset: 0; } }
                @keyframes scale { 0%, 100% { transform: none; } 50% { transform: scale3d(1.1, 1.1, 1); } }
                @keyframes fillSuccess { 100% { box-shadow: inset 0px 0px 0px 50px #4cd137; } }
                @keyframes fillError { 100% { box-shadow: inset 0px 0px 0px 50px #ff5252; } }
                @keyframes shake { 0%, 100% { transform: translateX(0); } 20%, 60% { transform: translateX(-5px); } 40%, 80% { transform: translateX(5px); } }
            `}</style>

            <div style={{ background: '#1a1a1a', padding: '50px 40px', borderRadius: '20px', border: '1px solid #333', textAlign: 'center', maxWidth: '450px', width: '90%', boxShadow: '0 10px 40px rgba(0,0,0,0.5)', animation: 'fadeIn 0.5s ease-out' }}>
                {renderIcon()}
                <h2 style={{ marginBottom: '15px', fontSize: '1.5rem', color: status === 'success' ? '#4cd137' : (status === 'error' ? '#ff5252' : '#fff'), transition: 'color 0.3s ease' }}>
                    {status === 'loading' ? 'Verificando...' : status === 'success' ? 'Conta Ativada!' : 'Link Expirado'}
                </h2>
                <p style={{color: '#aaa', lineHeight: '1.6', fontSize: '1rem', marginBottom: '25px'}}>
                    {status === 'success' ? 'Redirecionando...' : message}
                </p>
                {status === 'error' && (
                    <button onClick={() => navigate('/')} style={{ background: '#333', color: 'white', border: '1px solid #444', padding: '12px 24px', borderRadius: '10px', cursor: 'pointer', fontWeight: '600', fontSize: '0.9rem', transition: 'all 0.2s' }} onMouseOver={(e) => e.currentTarget.style.background = '#444'} onMouseOut={(e) => e.currentTarget.style.background = '#333'}>
                        Voltar para o Início
                    </button>
                )}
                {status === 'loading' && <div style={{color: '#555', fontSize: '0.8rem', marginTop: '10px'}}>Aguarde um momento</div>}
            </div>
        </div>
    );
};

export default VerifyEmailPage;